import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useEditor, EditorContent } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { Image } from '@/components/ui/minimal-tiptap/extensions'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import '@/components/ui/minimal-tiptap/styles/index.css'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

const NewsItem = ({
  item,
  inList = false,
}: {
  item: wowaudit.NewsItem
  inList?: boolean
}) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const isGuildNews = item.newsableType === 'Guild'

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: { HTMLAttributes: { class: 'block-node' } },
        bulletList: { HTMLAttributes: { class: 'list-node' } },
        code: { HTMLAttributes: { class: 'inline', spellcheck: 'false' } },
        codeBlock: false,
        heading: { HTMLAttributes: { class: 'heading-node' } },
        horizontalRule: false,
        link: {
          openOnClick: true,
          HTMLAttributes: { class: 'link' },
        },
        orderedList: { HTMLAttributes: { class: 'list-node' } },
        paragraph: { HTMLAttributes: { class: 'text-node' } },
      }),
      Image,
    ],
    content: item.content,
    editable: false,
    immediatelyRender: false,
  })

  return (
    <article className='border border-border rounded-md max-w-4xl bg-muted'>
      <header
        className={cn(
          'p-4 border-b',
          inList ? 'border-accent' : 'border-b border-border',
        )}
      >
        <div className='flex justify-between gap-2'>
          {inList ? (
            <h3 className='mb-1'>
              <Link to={`${team.path}/news/${item.id}`}>{item.title}</Link>
            </h3>
          ) : (
            <h2 className='mb-1'>{item.title}</h2>
          )}

          <div className='flex items-center gap-2'>
            {inList ? null : (
              <Link
                to={`${team.path}/news`}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                Back to news
              </Link>
            )}
            <ProtectedContent
              section='news'
              requirement='admin'
              renderUnauthorized={false}
              requireAllTeams={isGuildNews}
            >
              <Link
                to={`${team.path}/news/${item.id}/edit`}
                className={
                  inList
                    ? ''
                    : buttonVariants({ variant: 'outline', size: 'sm' })
                }
              >
                <Pencil className='w-4 h-4' />
              </Link>
            </ProtectedContent>
          </div>
        </div>

        <div className='flex items-center gap-2 text-muted-foreground'>
          {item.firstPublishedAt && (
            <time dateTime={item.firstPublishedAt}>
              {dayjs(item.firstPublishedAt).format('MMMM D, YYYY')}
            </time>
          )}
          {item.createdBy && (
            <>
              <span>•</span>
              <span>{item.createdBy.obfuscatedBattletag}</span>
            </>
          )}

          {isGuildNews ? null : (
            <>
              <span>•</span>
              <span>
                {t('shared.team')} {team.name}
              </span>
            </>
          )}
        </div>
      </header>

      {item.mainImageUrl && (
        <div className='overflow-hidden'>
          <img
            src={item.mainImageUrl}
            alt={item.title}
            className={cn(
              'w-full h-auto max-h-[500px] object-cover',
              !inList && 'rounded-md border border-border',
            )}
          />
        </div>
      )}

      <EditorContent
        editor={editor}
        className='minimal-tiptap-editor prose prose-invert max-w-none p-4'
      />
    </article>
  )
}

export default NewsItem
